import React, { useState, useEffect } from 'react';
import { gamesApi } from '../api/games';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import getGameType from '../utils/games';
import getGameImage from '../utils/getGameImage';

import CateImg1 from '../assets/images/home-cat-1.webp';
import CateImg2 from '../assets/images/home-cat-2.webp';
import CateImg3 from '../assets/images/home-cat-3.webp';
import LoadingPng from '../assets/images/Loading.png';
import Tabs, { TabItem } from './ui/Tab';

const categories = [
  { id: '1', icon: CateImg1, color: '0px 0px 10px 0px #FFE500' },
  { id: '2', icon: CateImg2, color: '0px 0px 10px 0px #0E64D1' },
  { id: '3', icon: CateImg3, color: '0px 0px 10px 0px #EF4343' },
];

export default function GameCategories({ categoriesHeight }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [games, setGames] = useState([]);
  const { tokens, user } = useAuth();
  const [loading, setLoading] = useState(true);

  const formatGamePrice = (winRate, commissionRate) => {
    if (user.roleId !== 9) {
      return `₱${parseInt(winRate)} | ${parseInt(commissionRate)}%`;
    } else {
      return `₱${parseInt(winRate)}`;
    }
  };

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      try {
        // Fetch all games to get the image mapping
        const allGames = await gamesApi.getAllGames(tokens.access.token);

        // Check if we got a redirected response (token expired)
        if (allGames && allGames.redirected) {
          console.log('Token expired during games fetch');
          setLoading(false);
          return;
        }

        const imageMap = allGames.data.reduce((acc, game) => {
          acc[game.id] = game.otherSettings.image;
          return acc;
        }, {});

        // Fetch user games
        const userGames = await gamesApi.getUserGames(tokens.access.token);

        // Check if we got a redirected response (token expired)
        if (userGames && userGames.redirected) {
          console.log('Token expired during user games fetch');
          setLoading(false);
          return;
        }

        const formattedGames = userGames.map((game) => {
          const image = imageMap[game.game_settings_id] || game.name;
          const gameType = getGameType(image);
          return {
            id: game.id,
            name: game.name,
            price: formatGamePrice(game.winRate, game.commissionRate),
            image: getGameImage(gameType),
            gameType,
            game_settings_id: game.game_settings_id,
          };
        });
        setLoading(false);
        setGames(formattedGames);
        sessionStorage.setItem('user_games', JSON.stringify(formattedGames));
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };
    fetchGames();
  }, [tokens.access.token]);

  const filterGamesByType = (typeId) => {
    if (typeId === 'all') return games;
    return games.filter((game) => game.gameType === typeId);
  };

  const uniqueGameTypes = [
    'all',
    ...new Set(games.map((game) => game.gameType)),
  ];

  return (
    <div className='flex justify-between gap-8 px-5 max-xs:gap-5'>
      {loading && (
        <div className='absolute inset-0 flex items-center justify-center bg-blue-500 bg-opacity-75'>
          <img
            src={LoadingPng}
            alt='Loading'
            className='w-16 h-16 animate-spin'
          />
        </div>
      )}
      <div className='w-[84px] max-xs:w-16 flex flex-col gap-3.5 max-xs:gap-0'>
        {categories.map((category) => (
          <div key={category.id} className='flex flex-col items-center'>
            <img
              src={category.icon}
              className='w-full max-xs:w-16 mb-1'
              onClick={() => setSelectedCategory(category.id)}
              style={{
                boxShadow:
                  selectedCategory === category.id ? category.color : 'none',
                borderRadius: '8px',
              }}
            />
            <span className='text-center text-sm text-gray-700'>
              {category.name}
            </span>
          </div>
        ))}
      </div>

      <div className='flex-1 overflow-x-auto hide-scrollbar'>
        <Tabs
          tabs={uniqueGameTypes.map((type) => ({ id: type, title: type }))}
          defaultActiveTab='all'
        >
          {uniqueGameTypes.map((tabId) => (
            <TabItem key={tabId} id={tabId}>
              <div className='grid grid-cols-3 gap-3.5'
              style={{
                    height: categoriesHeight > 0 ? `${categoriesHeight}px` : "auto",
                    overflowY: "auto"
                  }}
              >
                {filterGamesByType(tabId).map((game) => (
                  <Link
                    key={game.id}
                    to={`/bets/${game.id}`}
                    className='relative'
                  >
                    <div key={game.id} className='relative text-white shadow'>
                      <img src={game.image} className='w-full h-auto mb-0.5' />
                      <p
                        className='absolute top-0.5 left-0.5 text-black text-[8px] font-semibold w-90 px-1 rounded-sm'
                        style={{
                          background:
                            'linear-gradient(180deg, #FFE600 0%, #FC9703 60%)',
                          zIndex: 10,
                        }}
                      >
                        {game.price}
                      </p>
                      <div className='text-center'>
                        <p className='text-[10px] max-xs:text-[8px] font-semibold font-poppins'>
                          {game.name}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </TabItem>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
